import { z } from "zod";
import { channelDatasetFreshnessDeclarationSchema } from "./channel-data-trace.js";
import { channelObjectFieldTypeSchema } from "./channel-field-types.js";
const canonicalViewType = z.enum(["table", "matrix", "board", "calendar", "timeline", "dashboard"]);
export const sourceBackedChannelSchemas = ["mlx_curated", "connector_warehouse"];
const identifier = z.string().regex(/^[A-Za-z_][A-Za-z0-9_]*$/u, "Must be a SQL identifier.");
const fieldSchema = z.object({
    key: identifier, label: z.string().trim().min(1), type: channelObjectFieldTypeSchema,
    sourceColumn: identifier.optional(), valueOwner: z.enum(["source", "overlay"]).default("source"),
    required: z.boolean().default(false), position: z.number().int().min(0).max(10_000).default(0),
    config: z.record(z.string(), z.unknown()).default({}),
}).superRefine((field, ctx) => {
    if (field.valueOwner === "source" && !field.sourceColumn)
        ctx.addIssue({ code: "custom",
            path: ["sourceColumn"], message: `Source-backed field '${field.key}' requires sourceColumn.` });
});
const viewSchema = z.object({
    name: z.string().trim().min(1), slug: z.string().trim().min(1).optional(),
    type: canonicalViewType, config: z.record(z.string(), z.unknown()).default({}),
    definition: z.record(z.string(), z.unknown()).default({}), makeDefault: z.boolean().default(false),
});
const scopeValue = z.string().min(1).max(500);
/**
 * The rows a data set holds of its source: every condition must hold, as text, for a row to be in the data set at all.
 * The server applies it on every read of the data set (its views, records, lookups into it, its titles and exports),
 * so a channel can hold one project's slice of an organisation-wide report. A view's filters narrow what a reader is
 * shown; a row scope decides what the data set has.
 */
export const channelDatasetRowScopeSchema = z.array(z.discriminatedUnion("op", [
    z.object({ sourceColumn: identifier, op: z.literal("eq"), value: scopeValue }).strict(),
    z.object({ sourceColumn: identifier, op: z.literal("in"), values: z.array(scopeValue).min(1).max(100) }).strict(),
])).max(4);
const dataSetProjectionShape = {
    objectTypeName: z.string().trim().min(1),
    objectTypeSlug: z.string().trim().min(1).optional(), objectTypeDescription: z.string().default(""),
    titleColumn: identifier.optional(), sourceRefColumn: identifier.optional(),
    sourceCreatedAtColumn: identifier.optional(), sourceUpdatedAtColumn: identifier.optional(),
    fields: z.array(fieldSchema).min(1).max(80), views: z.array(viewSchema).max(12).default([]),
    freshness: channelDatasetFreshnessDeclarationSchema.optional(),
    // Omitted, a data set keeps the row scope it has (none when new); [] removes it.
    rowScope: channelDatasetRowScopeSchema.optional(),
};
const channelProjectionShape = {
    name: z.string().trim().min(1), slug: z.string().trim().min(1).optional(),
    description: z.string().default(""), purpose: z.string().default(""),
    visibility: z.enum(["private", "shared"]).default("shared"),
    externalIdColumn: identifier,
    ...dataSetProjectionShape,
};
const productSlugSchema = z.string().trim().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/u);
export const sourceBackedChannelInputSchema = z.object({
    ...channelProjectionShape,
    schemaName: z.enum(sourceBackedChannelSchemas), relationName: identifier,
    sourceSystem: z.string().regex(/^[a-zA-Z0-9._:-]+$/u),
}).superRefine((input, ctx) => {
    if (input.schemaName === "mlx_curated" && !input.relationName.startsWith("channel_")) {
        ctx.addIssue({ code: "custom", path: ["relationName"], message: "mlx_curated relations must use a customer-free channel_<domain>_<object_plural> name." });
    }
    validateProjectionInput(input, ctx);
});
export const twinProductChannelInputSchema = z.object({
    ...channelProjectionShape,
    // Omitted, a current Twin Build product's rows are identified by its grain, the columns that together key a row.
    externalIdColumn: identifier.optional(),
    productSlug: productSlugSchema,
}).superRefine(validateProjectionInput);
/**
 * A Twin Build product attached as a Data Set of an existing channel, or re-pointed under the Data Set with this
 * slug, which keeps its id, records' identities, fields and views. The channel keeps its name, description, purpose
 * and visibility.
 */
export const twinChannelProductDataSetInputSchema = z.object({
    ...dataSetProjectionShape,
    externalIdColumn: identifier.optional(),
    productSlug: productSlugSchema,
}).strict().superRefine(validateProjectionInput);
/** A data set definition that names a column its source lacks, or groups a board by a field that can't group it. */
export class SourceBackedChannelInputError extends Error {
    constructor(message) {
        super(message);
        this.name = "SourceBackedChannelInputError";
    }
}
export function normalizedSourceBackedViews(input) {
    // A view's slug is unique across its channel, so the default Table is named for its own data set; without a data
    // set slug the server derives the same one from the view's name.
    const views = input.views.length ? input.views : [{ name: `${input.objectTypeName} Table`,
            ...(input.objectTypeSlug ? { slug: `${input.objectTypeSlug}-table` } : {}),
            type: "table", config: {}, definition: { columns: input.fields.map((field) => field.key) },
            makeDefault: true }];
    return views.map((view) => {
        if (view.type !== "board")
            return view;
        const groupBy = stringValue(view.definition.groupBy) ?? stringValue(view.definition.groupByFieldKey)
            ?? stringValue(view.config.groupBy) ?? stringValue(view.config.groupByFieldKey);
        if (!groupBy)
            throw new SourceBackedChannelInputError("views.definition.groupBy is required for board views.");
        const field = input.fields.find((candidate) => candidate.key === groupBy);
        if (!field)
            throw new SourceBackedChannelInputError(`Board group field '${groupBy}' was not found in fields.`);
        if (field.type !== "select")
            throw new SourceBackedChannelInputError(`Board group field '${groupBy}' must be a select field.`);
        if (field.valueOwner === "overlay" && !field.sourceColumn && field.config.defaultValue === undefined) {
            throw new SourceBackedChannelInputError(`Board group field '${groupBy}' is MLX-owned but has no sourceColumn or config.defaultValue.`);
        }
        return { ...view, definition: { ...view.definition, groupBy, groupByFieldKey: groupBy } };
    });
}
export function validateSourceBackedColumns(sourceColumns, input) {
    for (const [name, column] of [["externalIdColumn", input.externalIdColumn], ["titleColumn", input.titleColumn],
        ["sourceRefColumn", input.sourceRefColumn], ["sourceCreatedAtColumn", input.sourceCreatedAtColumn],
        ["sourceUpdatedAtColumn", input.sourceUpdatedAtColumn]]) {
        if (column && !sourceColumns.has(column))
            throw new SourceBackedChannelInputError(`${name} '${column}' was not found on the curated source relation.`);
    }
    for (const column of input.externalIdColumns ?? []) {
        if (!sourceColumns.has(column))
            throw new SourceBackedChannelInputError(`externalIdColumns '${column}' was not found on the curated source relation.`);
    }
    for (const condition of input.rowScope ?? []) {
        if (!sourceColumns.has(condition.sourceColumn))
            throw new SourceBackedChannelInputError(`rowScope column '${condition.sourceColumn}' was not found on the curated source relation.`);
    }
    for (const field of input.fields) {
        if (field.sourceColumn && !sourceColumns.has(field.sourceColumn)) {
            throw new SourceBackedChannelInputError(`${field.valueOwner === "source" ? "Source-backed" : "Overlay"} field '${field.key}' sourceColumn '${field.sourceColumn}' was not found.`);
        }
    }
}
function validateProjectionInput(input, ctx) {
    const keys = new Set();
    for (const [index, field] of input.fields.entries()) {
        if (keys.has(field.key))
            ctx.addIssue({ code: "custom", path: ["fields", index, "key"],
                message: `Duplicate field key '${field.key}'.` });
        keys.add(field.key);
    }
}
function stringValue(value) {
    return typeof value === "string" && value.trim() ? value.trim() : undefined;
}
